// Modal with title, detail copy, and actions (up to 3, at least 1)
import React from 'react';
import { css } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type RestModalForwards,
  type RestModalMultiCTATypeI,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '@bbkFunnelComponents/modals/sections';
import { MultiCTAOtherActions } from '@bbkFunnelComponents/modals/v2/multi-cta-actions';

import './modal.scss';

type Props = {
  modalInfo: RestModalMultiCTATypeI;
  close: () => void;
  handleAction: (action: RestModalForwards) => void;
};

const RenderMultiCTATypeI: ReactFCC<Props> = (props) => {
  const {
    modalInfo: {
      id,
      requires: {
        info: { title, details },
        generic_actions = [],
      },
    },
    close,
    handleAction,
  } = props;
  const { mobileView, tabletView } = useMediaQueries();

  return (
    <ModalDesktopInnerSpacer
      css={css`
        display: grid;
        height: auto;
        ${tabletView && 'max-width: 750px; min-width: 500px'}
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
      `}
      className={cs('multi-cta-type-I display-grid inner-spacer', {
        desktop: tabletView,
        mobile: mobileView,
      })}
    >
      <OfferTitleMobile
        title={<TitleTheme title={title} data-part="title" />}
        close={close}
      />
      <div
        css={css`
          overflow-y: auto;
        `}
        data-part="description"
      >
        <CustomerHtml value={details} />
      </div>
      <div
        className="display-grid action-items"
        css={css`
          gap: 1rem;
          ${
            tabletView &&
            css`
              grid-template-columns: repeat(${generic_actions.length}, 1fr);
              grid-template-rows: auto;
            `
          }
        `}
      >
        <MultiCTAOtherActions
          actions={generic_actions}
          handleAction={handleAction}
        />
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const MultiCTATypeI = Object.assign(RenderMultiCTATypeI, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      options: { formatting: false },
      selector: '[data-part="title"]',
      placement: 'right',
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    actions: {
      path: ['requires', 'generic_actions'],
      control: 'ctaAppearance',
      label: 'Action',
      selector: '[data-part="cta"]',
      indexAttribute: 'data-action-index',
      placement: 'top-end',
    },
  } satisfies InlineEditorHints,
});
